import React, { useEffect, useMemo, useState } from 'react';
import {
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Box,
  Typography,
  IconButton,
  Divider,
} from '@mui/material';
import { getAttachments } from '../services/attachment.service';
import { toast } from 'react-toastify';

interface Attachment {
  id: number;
  name: string;
  case_type?: string | null;
}

function attachmentAppliesToServiceCaseType(
  attachment: Attachment,
  serviceCaseType: string
): boolean {
  if (!serviceCaseType) return true;
  if (!attachment.case_type) return true;
  return attachment.case_type === serviceCaseType;
}

interface ServiceTypeFormProps {
  initialValues?: any;
  onSubmit: (data: any) => void;
  loading?: boolean;
}

interface RushTier {
  key: 'tier_1' | 'tier_2' | 'tier_3';
  label: string;
  min_days: number | string;
  max_days: number | string;
  fee: number | string;
}

const TIER_KEYS: RushTier['key'][] = ['tier_1', 'tier_2', 'tier_3'];
const TIER_LABELS: Record<RushTier['key'], string> = {
  tier_1: 'Tier 1',
  tier_2: 'Tier 2',
  tier_3: 'Tier 3',
};

interface FormData {
  name: string;
  short_code: string;
  description: string;
  case_type: string;
  price: string;
  rush_tiers: RushTier[];
  attachments: number[];
}

const ServiceTypeForm: React.FC<ServiceTypeFormProps> = ({
  initialValues = {},
  onSubmit,
  loading,
}) => {
  const [form, setForm] = useState<FormData>(() => {
    const attachmentIds = Array.isArray(initialValues?.attachments)
      ? initialValues.attachments.map((a: any) => (typeof a === 'object' ? a.id : a))
      : [];
    return {
      name: initialValues?.name ?? '',
      short_code: initialValues?.short_code ?? '',
      description: initialValues?.description ?? '',
      case_type: initialValues?.case_type ?? '',
      price: initialValues?.price ?? '',
      rush_tiers: Array.isArray(initialValues?.rush_tiers) ? initialValues.rush_tiers : [],
      attachments: attachmentIds,
    };
  });
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);

  // Update form when initialValues change
  useEffect(() => {
    if (initialValues) {
      const caseType = initialValues.case_type ?? '';
      const attachmentIds = Array.isArray(initialValues.attachments)
        ? initialValues.attachments.map((a: any) =>
            typeof a === 'object' ? a.id : a
          )
        : [];

      setForm((prev: FormData) => ({
        ...prev,
        ...initialValues,
        case_type: caseType,
        attachments: attachmentIds,
        rush_tiers: Array.isArray(initialValues.rush_tiers) ? initialValues.rush_tiers : [],
      }));
    }
  }, [initialValues]);

  const filteredAttachments = useMemo(
    () =>
      attachments.filter(a =>
        attachmentAppliesToServiceCaseType(a, form.case_type)
      ),
    [attachments, form.case_type]
  );

  // Prune invalid attachment selections once attachments are loaded
  useEffect(() => {
    if (attachments.length === 0) return;

    setForm(prev => {
      const validIds = new Set(
        attachments
          .filter(a => attachmentAppliesToServiceCaseType(a, prev.case_type))
          .map(a => a.id)
      );
      const pruned = prev.attachments.filter(id => validIds.has(id));
      if (pruned.length === prev.attachments.length) return prev;
      return { ...prev, attachments: pruned };
    });
  }, [attachments, form.case_type]);

  useEffect(() => {
    setLoadingAttachments(true);
    getAttachments()
      .then(res => setAttachments(res.data))
      .catch(error => {
        console.error('Error fetching attachments:', error);
        toast.error('Failed to load attachments');
      })
      .finally(() => setLoadingAttachments(false));
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { name: string; value: unknown } }
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (e: { target: { name: string; value: unknown } }) => {
    const { name, value } = e.target;

    if (name === 'case_type') {
      const nextCaseType = value as string;
      const validIds = new Set(
        attachments
          .filter(a => attachmentAppliesToServiceCaseType(a, nextCaseType))
          .map(a => a.id)
      );
      setForm(prev => ({
        ...prev,
        case_type: nextCaseType,
        attachments: prev.attachments.filter(id => validIds.has(id)),
      }));
      return;
    }

    handleChange(e);
  };

  const handleAttachmentsChange = (event: any) => {
    const { value } = event.target;
    const attachmentIds = (
      typeof value === 'string' ? value.split(',').map(Number) : value
    ).filter((id: number) => !isNaN(id) && id !== null);
    setForm({ ...form, attachments: attachmentIds });
  };

  // ── Rush Tier helpers ──────────────────────────────────────────────────
  const activeTierKeys = form.rush_tiers.map(t => t.key);

  const addTier = (key: RushTier['key']) => {
    if (activeTierKeys.includes(key)) return;
    setForm(prev => ({
      ...prev,
      rush_tiers: [
        ...prev.rush_tiers,
        { key, label: TIER_LABELS[key], min_days: '', max_days: '', fee: '' },
      ].sort((a, b) => TIER_KEYS.indexOf(a.key) - TIER_KEYS.indexOf(b.key)),
    }));
  };

  const removeTier = (key: RushTier['key']) => {
    setForm(prev => ({
      ...prev,
      rush_tiers: prev.rush_tiers.filter(t => t.key !== key),
    }));
  };

  const updateTierField = (
    key: RushTier['key'],
    field: keyof Omit<RushTier, 'key'>,
    value: string
  ) => {
    setForm(prev => ({
      ...prev,
      rush_tiers: prev.rush_tiers.map(t =>
        t.key === key ? { ...t, [field]: value } : t
      ),
    }));
  };

  // ── Submit ─────────────────────────────────────────────────────────────
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const submitData = {
      ...form,
      price: Number(form.price),
      rush_tiers: form.rush_tiers.map(t => ({
        key: t.key,
        label: t.label,
        min_days: Number(t.min_days),
        max_days: Number(t.max_days),
        fee: Number(t.fee),
      })),
      attachments: form.attachments.filter(
        (id: number) => !isNaN(id) && id !== null
      ),
    };
    onSubmit(submitData);
  };

  return (
    <Box
      component='form'
      onSubmit={handleSubmit}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: 400 }}
    >
      <TextField
        label='Service Type Name'
        name='name'
        value={form.name}
        onChange={handleChange}
        required
        fullWidth
      />
      <TextField
        label='Short Code'
        name='short_code'
        value={form.short_code}
        onChange={handleChange}
        required
        fullWidth
      />
      <TextField
        label='Description'
        name='description'
        value={form.description}
        onChange={handleChange}
        multiline
        rows={2}
        fullWidth
      />
      <FormControl fullWidth>
        <InputLabel id='case-type-label'>Case Type</InputLabel>
        <Select
          labelId='case-type-label'
          name='case_type'
          value={form.case_type}
          label='Case Type'
          onChange={handleSelectChange}
        >
          <MenuItem value=''>Both (Plaintiff &amp; Defendant)</MenuItem>
          <MenuItem value='Plaintiff'>Plaintiff Only</MenuItem>
          <MenuItem value='Defendant'>Defendant Only</MenuItem>
        </Select>
      </FormControl>
      <TextField
        label='Price'
        name='price'
        value={form.price}
        onChange={handleChange}
        type='number'
        required
        fullWidth
        inputProps={{ min: 0, step: 0.01 }}
      />

      {/* ── Rush Tiers ──────────────────────────────────────────────────── */}
      <Divider />
      <Box>
        <Typography variant='subtitle2' fontWeight={700} sx={{ mb: 0.5 }}>
          Rush Tiers
        </Typography>
        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1.5 }}>
          Configure tiered rush fees for this service type. Each tier defines a turnaround window and the additional fee charged.
        </Typography>

        {/* Active tiers */}
        {form.rush_tiers.map(tier => (
          <Box
            key={tier.key}
            sx={{
              border: '1px solid',
              borderColor: 'error.light',
              borderRadius: 1,
              p: 1.5,
              mb: 1.5,
              bgcolor: 'error.50',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant='subtitle2' color='error.dark' fontWeight={700}>
                {tier.label}
              </Typography>
              <IconButton size='small' onClick={() => removeTier(tier.key)} color='error'>
                ✕
              </IconButton>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
              <TextField
                label='Min Days'
                type='number'
                size='small'
                value={tier.min_days}
                onChange={e => updateTierField(tier.key, 'min_days', e.target.value)}
                inputProps={{ min: 1 }}
                required
              />
              <TextField
                label='Max Days'
                type='number'
                size='small'
                value={tier.max_days}
                onChange={e => updateTierField(tier.key, 'max_days', e.target.value)}
                inputProps={{ min: 1 }}
                required
              />
              <TextField
                label='Fee ($)'
                type='number'
                size='small'
                value={tier.fee}
                onChange={e => updateTierField(tier.key, 'fee', e.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
                required
              />
            </Box>
          </Box>
        ))}

        {/* Add tier buttons */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {TIER_KEYS.filter(k => !activeTierKeys.includes(k)).map(k => (
            <Button
              key={k}
              size='small'
              variant='outlined'
              color='error'
              onClick={() => addTier(k)}
            >
              + {TIER_LABELS[k]}
            </Button>
          ))}
          {activeTierKeys.length === TIER_KEYS.length && (
            <Typography variant='caption' color='text.secondary' sx={{ alignSelf: 'center' }}>
              All tiers added
            </Typography>
          )}
        </Box>
      </Box>
      <Divider />

      <FormControl fullWidth>
        <InputLabel id='attachments-label'>Attachments</InputLabel>
        <Select
          labelId='attachments-label'
          multiple
          value={form.attachments}
          onChange={handleAttachmentsChange}
          input={<OutlinedInput label='Attachments' />}
          renderValue={selected =>
            attachments
              .filter(a => selected.includes(a.id))
              .map(a => a.name)
              .join(', ') || 'Select attachments'
          }
          disabled={loadingAttachments}
        >
          {filteredAttachments.length === 0 ? (
            <MenuItem disabled value=''>
              {form.case_type
                ? `No attachments available for ${form.case_type}`
                : 'No attachments available'}
            </MenuItem>
          ) : (
            filteredAttachments.map(attachment => (
              <MenuItem key={attachment.id} value={attachment.id}>
                <Checkbox checked={form.attachments.includes(attachment.id)} />
                <ListItemText
                  primary={attachment.name}
                  secondary={attachment.case_type ? attachment.case_type : 'Both case types'}
                />
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
      <Button
        type='submit'
        variant='contained'
        disabled={loading || loadingAttachments}
        fullWidth
      >
        {loading ? 'Submitting...' : 'Submit'}
      </Button>
    </Box>
  );
};

export default ServiceTypeForm;
